/**
 * V186 — Axios interceptor that auto-attaches a valid switcher coupon
 * (from `localStorage.trp_switcher_coupon`) to any POST that hits a
 * checkout endpoint (`/payments/checkout`, `/billing/subscription`).
 *
 * Idempotent: only injects when:
 *   - the request body is a plain object (skips form data)
 *   - the body has NO existing `coupon` set
 *   - the coupon hasn't expired
 *
 * Imported once at app bootstrap (next to `axios-setup`).
 */
import axios from "axios";

const STORAGE_KEY = "trp_switcher_coupon";
const CHECKOUT_PATHS = ["/payments/checkout", "/billing/subscription", "/billing/checkout"];

function readCoupon() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (!c?.code) return null;
    if (c.expires_at && new Date(c.expires_at).getTime() < Date.now()) return null;
    return c;
  } catch {
    return null;
  }
}

axios.interceptors.request.use(req => {
  try {
    const url = String(req.url || "");
    if ((req.method || "").toLowerCase() !== "post") return req;
    if (!CHECKOUT_PATHS.some(p => url.includes(p))) return req;

    const body = req.data;
    if (!body || typeof body !== "object" || Array.isArray(body)) return req;
    if (body.coupon) return req; // explicit caller coupon wins

    const c = readCoupon();
    if (!c) return req;

    req.data = { ...body, coupon: c.code };
    // Stamp the source slug into metadata so the redemption can be
    // attributed back to the /vs/<slug> page that issued it.
    req.data.metadata = {
      ...(body.metadata || {}),
      switcher_from_slug: c.from_slug || "",
      switcher_issued_at: c.issued_at || "",
    };
  } catch {
    // Never break the request just because we couldn't attach a coupon.
  }
  return req;
}, e => Promise.reject(e));

export {};
